import { Box, Stack, Typography } from '@mui/material';
import { BikeleasingContractDetailsDTO, BikeleasingContractDetailsDTOStatusEnum } from 'probonio-shared-ui/api';
import { useBikeleasingPortalLinkBuilder } from 'probonio-shared-ui/utils/bikeleasingUrl';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLinkButton } from '../../../component/button/ExternalLinkButton';
import { InfoAccordion } from '../../../component/infoAccordion/InfoAccordion';
import { InfoRow } from '../../../component/infoAccordion/InfoRow';
import { useBikeleasingDamageReportUrl } from '../useBikeleasingLinks';

const AllDamageReports: React.FC<{ contractId: string }> = props => {
  const { t } = useTranslation('extensions');
  const buildBikeleasingPortalLink = useBikeleasingPortalLinkBuilder();
  const damageReportUrl = useBikeleasingDamageReportUrl();

  return (
    <Box sx={{ mx: 1 }}>
      <Typography sx={{ mb: 0.5, mt: 1 }}>{t('extensions:bikeleasing_v2.contract.damageReportInfo')}</Typography>
      <ExternalLinkButton href={damageReportUrl} variant="text" sx={{ p: 0 }}>
        {t('bikeleasing_v2.contract.moreInformation')}
      </ExternalLinkButton>

      <Stack spacing={1} sx={{ mt: 1, mb: 1 }}>
        <ExternalLinkButton href={buildBikeleasingPortalLink(`/user/report-damage/${props.contractId}/2`)} fullWidth variant="contained">
          {t('bikeleasing_v2.contract.buttons.reportDamage')}
        </ExternalLinkButton>

        <ExternalLinkButton href={buildBikeleasingPortalLink(`/user/employee/insurances/all`)} fullWidth>
          {t('bikeleasing_v2.contract.buttons.allDamageReports')}
        </ExternalLinkButton>
      </Stack>
    </Box>
  );
};

export const BikeData: React.FC<{ contract: BikeleasingContractDetailsDTO }> = props => {
  const { t } = useTranslation('benefitModule');
  const { status, bike, accessories, leasingData } = props.contract;

  const renderEuro = useCallback(
    (value: number | undefined): string => (value !== undefined ? t('common:money', { money: value }) : ''),
    [t],
  );

  return (
    <InfoAccordion title={t('extensions:bikeleasing_v2_benefit.section.bike')}>
      <InfoRow key="brand" label={t('extensions:bikeleasing_v2_benefit.contract.brand')} info={bike.brand} />
      <InfoRow key="model" label={t('extensions:bikeleasing_v2_benefit.contract.model')} info={bike.model} />
      <InfoRow key="frameNumber" label={t('extensions:bikeleasing_v2.contract.frameNumber')} info={bike.frameNo} />
      <InfoRow key="frameSize" label={t('extensions:bikeleasing_v2_benefit.contract.frameSize')} info={bike.frameSize} />
      <InfoRow key="bikeType" label={t('extensions:bikeleasing_v2.contract.bikeType')} info={bike.bikeType} />
      <InfoRow key="bikeColor" label={t('extensions:bikeleasing_v2.contract.bikeColor')} info={bike.color} />
      <InfoRow key="accessories" label={t('extensions:bikeleasing_v2.contract.accessories')} info={accessories.description} />
      <InfoRow key="trailer" label={t('extensions:bikeleasing_v2.contract.trailer')} info={renderEuro(leasingData?.rrpTrailer?.amount)} />
      <InfoRow key="lockBrand" label={t('extensions:bikeleasing_v2_benefit.contract.lockBrand')} info={accessories.lockBrand} />
      <InfoRow key="lockType" label={t('extensions:bikeleasing_v2.contract.lockType')} info={bike.typeOfLock} />
      {(status === BikeleasingContractDetailsDTOStatusEnum.Active || status === BikeleasingContractDetailsDTOStatusEnum.Expiring) && (
        <AllDamageReports key="damageReports" contractId={props.contract.id} />
      )}
    </InfoAccordion>
  );
};
